import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../services/api";
import { Mail } from "lucide-react";

const Contact: React.FC = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const MAX_MESSAGE = 2000;

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!user && !email.trim()) {
      setError("Email required for non-logged users");
      return;
    }
    if (!message.trim()) {
      setError("Message is required");
      return;
    }

    try {
      setLoading(true);
      const payload: any = { subject: subject.trim(), message: message.trim() };
      if (!user) payload.email = email.trim();
      const res = await api.post("/api/contact", payload);
      setSuccess(res.data.message || "Message sent");
      setSubject("");
      setMessage("");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Error sending message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="beat-container max-w-2xl mx-auto">
      <div className="beat-card p-8 rounded-lg">
        <div className="text-center mb-6">
          <div className="flex justify-center items-center mb-3">
            <Mail className="w-10 h-10 text-newspaper-900" />
          </div>
          <h1 className="text-2xl font-bold text-newspaper-900">Contact</h1>
          <p className="text-newspaper-600">Send us your message and we’ll reply</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!user && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-newspaper-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="beat-input rounded-lg"
                placeholder="your.email@example.com"
                required
              />
            </div>
          )}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-newspaper-700 mb-2">
              Subject (optional)
            </label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="beat-input rounded-lg"
              placeholder="Message topic"
              maxLength={140}
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-newspaper-700 mb-2">
              Message
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="beat-input rounded-lg min-h-[150px]"
              placeholder="Write your message"
              maxLength={MAX_MESSAGE}
              required
            />
            <div
              className={`text-xs text-right mt-1 ${
                message.length > MAX_MESSAGE - 100 ? "text-red-600" : "text-newspaper-500"
              }`}
              aria-live="polite"
            >
              {message.length}/{MAX_MESSAGE}
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full beat-button py-3 rounded-lg">
            {loading ? <div className="beat-spinner"></div> : "Send"}
          </button>
          {(success || error) && (
            <div className="text-center">
              {success && <p className="text-green-600 text-sm">{success}</p>}
              {error && <p className="text-red-600 text-sm">{error}</p>}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Contact;
